import React, { useState, useEffect } from "react";
import {
  FormControl, Select, MenuItem, InputLabel, Button, Popover,
  TextField, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip
} from "@mui/material";
import { Grid, Box, Typography, Paper, Container } from "@mui/material";
import axios from "axios";
import sacaqmLogo from '../assets/sacaqm_logo.png';

import { useNavigate } from "react-router-dom";
import PMWidget from "../components/envDashboard/PMWidget";
import NoiseGauge from "../components/envDashboard/NoiseGauge";
import NoiseWidget from "../components/envDashboard/NoiseWidget";
import TempWidget from "../components/envDashboard/TempWidget";
import ParameterWidget from "../components/envDashboard/ParameterWidget";
import ExceedancesOverTimeChart from "../components/envDashboard/ExceedancesOverTimeChart";
import ExceedancesTable from "../components/envDashboard/ExceedancesTable";
import ExceedancesSeverityChart from "../components/envDashboard/ExceedancesSeverityChart";
import generateReport from "../utils/generateReport";

const MIDRANGES_BASE = (process.env.REACT_APP_MIDRANGES_API_BASE || "").replace(/\/+$/, "");
const HOTEL_READ_KEY = process.env.REACT_APP_HOTEL_READ_KEY;
const HOTEL_AUTH_HEADERS = { Authorization: `Bearer ${HOTEL_READ_KEY}` };

const FORECAST_HOUR_LABELS = Array.from({ length: 24 }, (_, i) => {
  const h = i % 12 || 12;
  const ampm = i < 12 ? 'AM' : 'PM';
  return `${h} ${ampm}`;
});
const FORECAST_DAY_LABEL = (() => {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
})();

function formatDate(d) {
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}
function calcTrend(curr, prev) {
  if (!prev?.length) return 0;
  const ca = curr.reduce((s, v) => s + v, 0) / curr.length, pa = prev.reduce((s, v) => s + v, 0) / prev.length;
  return pa === 0 ? 0 : Math.round(((ca - pa) / pa) * 100);
}
function getPrevPeriod(start, end) {
  const s = new Date(start), e = new Date(end), days = Math.ceil(Math.abs(e - s) / 86400000);
  const pe = new Date(s); pe.setDate(pe.getDate() - 1);
  const ps = new Date(pe); ps.setDate(ps.getDate() - days);
  return { start: formatDate(ps), end: formatDate(pe) };
}

const AQI_BANDS = {
  pm25: [{ max: 103, status: "Green" }, { max: 128, status: "Yellow" }, { max: 178, status: "Orange" }, { max: Infinity, status: "Red" }],
  pm10: [{ max: 190, status: "Green" }, { max: 240, status: "Yellow" }, { max: 290, status: "Orange" }, { max: Infinity, status: "Red" }],
  noise: [{ max: 70, status: "Green" }, { max: 85, status: "Yellow" }, { max: 100, status: "Orange" }, { max: Infinity, status: "Red" }],
  pm5: [{ max: 103, status: "Green" }, { max: 128, status: "Yellow" }, { max: 178, status: "Orange" }, { max: Infinity, status: "Red" }],
};

function statusFor(val, thr, key) {
  if (thr == null) return "—";
  const bands = AQI_BANDS[key];
  if (bands) { for (const b of bands) if (val <= b.max) return b.status; }
  if (val <= thr) return "Green"; if (val <= thr * 1.2) return "Yellow"; if (val <= thr * 1.5) return "Orange"; return "Red";
}
const avgF = (arr, key) => arr.length ? Math.round(arr.reduce((s, d) => s + (d[key] || 0), 0) / arr.length) : 0;
const sMin = (arr) => arr?.length ? Math.round(Math.min(...arr.map(d => d.min ?? 0))) : 0;
const sMax = (arr) => arr?.length ? Math.round(Math.max(...arr.map(d => d.max ?? 0))) : 0;

const THRESHOLDS = { pm1: 103, pm25: 103, pm5: 103, pm10: 190, noise: 85, temperature: 32, humidity: 85, co2: 1000, nox: null, voc: null };
const DAILY_THRESHOLDS = { pm1: 40, pm25: 40, pm5: 40, pm10: 75 };

const filterBarSx = {
  background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px) saturate(180%)',
  border: '1px solid rgba(59,130,246,0.2)', p: 3, borderRadius: 4, mb: 3,
  boxShadow: '0 8px 32px rgba(59,130,246,0.1)', position: 'relative', zIndex: 10, overflow: 'hidden',
  '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg,#3b82f6,#6366f1,#0ea5e9)' },
};
const selectSx = {
  bgcolor: "white", borderRadius: 2.5,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(59,130,246,0.2)", borderWidth: 2 },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6", borderWidth: 2 },
  fontSize: "0.95rem", fontWeight: 500, boxShadow: "0 2px 8px rgba(59,130,246,0.08)",
  "&:hover": { boxShadow: "0 4px 12px rgba(59,130,246,0.15)" },
};
const presetSx = {
  px: 2, py: 0.5, fontSize: "0.75rem", fontWeight: 600, textTransform: "none",
  borderRadius: 2, border: "2px solid transparent", bgcolor: "rgba(59,130,246,0.1)", color: "#3b82f6",
  "&:hover": { bgcolor: "#3b82f6", color: "white" },
};
const labelSx = { fontSize: "0.75rem", fontWeight: 700, color: "#64748b", letterSpacing: "0.5px", textTransform: "uppercase" };
const fadeIn = (d) => ({
  animation: `fadeInUp 0.6s ease-out ${d}s`, animationFillMode: "backwards",
  "@keyframes fadeInUp": { from: { opacity: 0, transform: "translateY(30px)" }, to: { opacity: 1, transform: "translateY(0)" } },
});

function buildWidgets(curr, prevData, labels, mm1, mm25, mm4, mm10) {
  const pmData = {
    pm1: { title: "PM1.0", labels, values: curr.map(d => Math.round(d.pm1p0 || 0)), current: avgF(curr, "pm1p0"), trend: calcTrend(curr.map(d => d.pm1p0 || 0), prevData.map(d => d.pm1p0 || 0)), min: sMin(mm1), max: sMax(mm1) },
    pm25: { title: "PM2.5", labels, values: curr.map(d => Math.round(d.pm2p5 || 0)), current: avgF(curr, "pm2p5"), trend: calcTrend(curr.map(d => d.pm2p5 || 0), prevData.map(d => d.pm2p5 || 0)), min: sMin(mm25), max: sMax(mm25) },
    pm5: { title: "PM4.0", labels, values: curr.map(d => Math.round(d.pm4p0 || 0)), current: avgF(curr, "pm4p0"), trend: calcTrend(curr.map(d => d.pm4p0 || 0), prevData.map(d => d.pm4p0 || 0)), min: sMin(mm4), max: sMax(mm4) },
    pm10: { title: "PM10", labels, values: curr.map(d => Math.round(d.pm10p0 || 0)), current: avgF(curr, "pm10p0"), trend: calcTrend(curr.map(d => d.pm10p0 || 0), prevData.map(d => d.pm10p0 || 0)), min: sMin(mm10), max: sMax(mm10) },
  };
  return {
    pmData,
    noiseData: { labels, values: curr.map(d => Math.round(d.dba || 0)), current: avgF(curr, "dba") },
    tempData: { labels, values: curr.map(d => Math.round(d.temperature || 0)), current: avgF(curr, "temperature"), trend: calcTrend(curr.map(d => d.temperature || 0), prevData.map(d => d.temperature || 0)) },
    humidityData: { labels, values: curr.map(d => Math.round(d.humidity || 0)), current: avgF(curr, "humidity"), trend: calcTrend(curr.map(d => d.humidity || 0), prevData.map(d => d.humidity || 0)) },
    co2Data: { labels, values: curr.map(d => Math.round(d.co2 || 0)), current: avgF(curr, "co2"), trend: calcTrend(curr.map(d => d.co2 || 0), prevData.map(d => d.co2 || 0)) },
    noxData: { labels, values: curr.map(d => Math.round(d.nox || 0)), current: avgF(curr, "nox"), trend: calcTrend(curr.map(d => d.nox || 0), prevData.map(d => d.nox || 0)) },
    vocData: { labels, values: curr.map(d => Math.round(d.voc || 0)), current: avgF(curr, "voc"), trend: calcTrend(curr.map(d => d.voc || 0), prevData.map(d => d.voc || 0)) },
  };
}

export default function HotelCompliance() {
  const navigate = useNavigate();

  const [stations, setStations] = useState([]);
  const [stationsLoading, setStationsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${MIDRANGES_BASE}/api/hotel-locations`, { headers: HOTEL_AUTH_HEADERS })
      .then(({ data }) => {
        if (!mounted) return;
        const list = Array.isArray(data)
          ? data.map(loc => ({ _id: loc._id, name: loc.name, sensorIds: loc.macAddress ? [loc.macAddress] : [] }))
          : [];
        setStations(list);
      })
      .catch(() => { if (mounted) setStations([]); })
      .finally(() => { if (mounted) setStationsLoading(false); });
    return () => { mounted = false; };
  }, []);

  const [pinnedSensorId] = useState(() => {
    const v = sessionStorage.getItem("hotelComplianceSensorId") || "";
    sessionStorage.removeItem("hotelComplianceSensorId");
    return v;
  });
  const [pinnedStationId] = useState(() => {
    const v = sessionStorage.getItem("hotelComplianceStationId") || "";
    sessionStorage.removeItem("hotelComplianceStationId");
    return v;
  });

  const [sensorId, setSensorId] = useState("");
  const [startDate, setStartDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 14); return formatDate(d); });
  const [endDate, setEndDate] = useState(() => formatDate(new Date()));
  const [dateLabel, setDateLabel] = useState(() => {
    const d = new Date(), s = new Date(d); s.setDate(d.getDate() - 14);
    return `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${d.getFullYear()}`;
  });
  const [resolution, setResolution] = useState("daily");
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [anchor, setAnchor] = useState(null);
  const [showForecast, setShowForecast] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [realtimeNoise, setRealtimeNoise] = useState(null);

  const [forecastData, setForecastData] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState(null);

  const hasAppliedPinned = React.useRef(false);

  const getDesignatedSensorId = (st) => {
    const ids = st.sensorIds || [];
    return ids.length ? ids[ids.length - 1] : null;
  };

  const sensorOptions = (stations || []).map(st => {
    const sid = getDesignatedSensorId(st);
    return sid ? { id: sid, label: st.name } : null;
  }).filter(Boolean);

  useEffect(() => {
    if (hasAppliedPinned.current) {
      if (!sensorId && sensorOptions.length > 0) setSensorId(sensorOptions[0].id);
      return;
    }

    if (pinnedStationId) {
      if (sensorOptions.length === 0 || stationsLoading) return;
      const matchedStation = stations.find(st => st._id === pinnedStationId);
      if (matchedStation && matchedStation.sensorIds?.length > 0) {
        const desired = getDesignatedSensorId(matchedStation);
        if (desired) {
          setSensorId(desired);
          hasAppliedPinned.current = true;
        }
        return;
      }
    }

    if (pinnedSensorId) {
      setSensorId(pinnedSensorId);
      hasAppliedPinned.current = true;
      return;
    }

    if (sensorOptions.length > 0 && !sensorId) {
      setSensorId(sensorOptions[0].id);
      hasAppliedPinned.current = true;
    }
  }, [stations, pinnedSensorId, pinnedStationId, sensorId, stationsLoading]);

  useEffect(() => { if (sensorId) fetchDashboard(); }, [sensorId, startDate, endDate, resolution]);

  useEffect(() => {
    if (!sensorId) return;
    setForecastData(null);
    setForecastError(null);
    setShowForecast(false);
  }, [sensorId]);

  useEffect(() => {
    if (showForecast && !forecastData && !forecastLoading && !forecastError && sensorId) {
      loadForecast(sensorId);
    }
  }, [showForecast, forecastData, forecastLoading, forecastError, sensorId]);

  useEffect(() => {
    if (!sensorId) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${MIDRANGES_BASE}/api/hotel-readings`, {
          params: { sensor_id: sensorId, start: formatDate(new Date()), end: formatDate(new Date()), resolution: 'hourly' },
          headers: HOTEL_AUTH_HEADERS
        });
        const recs = res.data || [];
        for (let i = recs.length - 1; i >= 0; i--) { if ((recs[i].dba || 0) > 0) { setRealtimeNoise(Math.round(recs[i].dba)); return; } }
        setRealtimeNoise(null);
      } catch { setRealtimeNoise(null); }
    };
    poll(); const iv = setInterval(poll, 5 * 60 * 1000); return () => clearInterval(iv);
  }, [sensorId]);

  async function loadForecast(sid) {
    setForecastLoading(true);
    setForecastError("AI forecast is not available for this site yet.");
    setForecastLoading(false);
    setShowForecast(false);
  }

  async function fetchDashboard(overrideStart, overrideEnd, overrideRes) {
    setLoading(true); setError(null);
    const qStart = overrideStart || startDate;
    const qEnd   = overrideEnd   || endDate;
    const qRes   = overrideRes   || resolution;
    try {
      const prev = getPrevPeriod(qStart, qEnd);
      const [cR, hR, pR] = await Promise.all([
        axios.get(`${MIDRANGES_BASE}/api/hotel-readings`, {
          params: { sensor_id: sensorId, start: qStart, end: qEnd, resolution: qRes },
          headers: HOTEL_AUTH_HEADERS
        }),
        axios.get(`${MIDRANGES_BASE}/api/hotel-readings`, {
          params: { sensor_id: sensorId, start: qStart, end: qEnd, resolution: 'hourly' },
          headers: HOTEL_AUTH_HEADERS
        }),
        axios.get(`${MIDRANGES_BASE}/api/hotel-readings`, {
          params: { sensor_id: sensorId, start: prev.start, end: prev.end, resolution: qRes },
          headers: HOTEL_AUTH_HEADERS
        }).catch(() => ({ data: [] })),
      ]);
      const curr = cR.data || [], hourly = hR.data || [], prevData = pR.data || [];

      if (!curr.length && !overrideStart) {
        const today = new Date();
        const fallbackWindows = [30, 60, 90];
        for (const days of fallbackWindows) {
          const fb = new Date(today); fb.setDate(today.getDate() - days);
          const fbStart = formatDate(fb);
          const fbEnd = formatDate(today);
          const fbRes = await axios.get(`${MIDRANGES_BASE}/api/hotel-readings`, {
            params: { sensor_id: sensorId, start: fbStart, end: fbEnd, resolution: 'daily' },
            headers: HOTEL_AUTH_HEADERS
          }).catch(() => ({ data: [] }));
          if (fbRes.data && fbRes.data.length > 0) {
            setStartDate(fbStart);
            setEndDate(fbEnd);
            const s = fb, e = today;
            setDateLabel(`${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${e.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${e.getFullYear()}`);
            setLoading(false);
            fetchDashboard(fbStart, fbEnd, 'daily');
            return;
          }
        }
        const sensorLabel = sensorOptions.find(o => o.id === sensorId)?.label || sensorId;
        setError(`No data found for "${sensorLabel}". The sensor may not have reported recently.`);
        setDashData(null); setLoading(false); return;
      }

      if (!curr.length) {
        const sensorLabel = sensorOptions.find(o => o.id === sensorId)?.label || sensorId;
        setError(`No data for "${sensorLabel}" between ${qStart} and ${qEnd}.`);
        setDashData(null); setLoading(false); return;
      }

      const mmF = ["pm1p0", "pm2p5", "pm4p0", "pm10p0", "dba", "temperature", "humidity", "co2", "nox", "voc"];
      const mmRes = await Promise.allSettled(mmF.map(f =>
        axios.get(`${MIDRANGES_BASE}/api/hotel-readings-minmax`, {
          params: { sensor_id: sensorId, start: startDate, end: endDate, field: f },
          headers: HOTEL_AUTH_HEADERS
        })
      ));
      const [mm1, mm25, mm4, mm10] = mmRes.map(r => r.status === "fulfilled" ? r.value.data || [] : []);

      const isSingleDay = startDate === endDate;
      const labels = curr.map(item => {
        const d = new Date(item.timestamp);
        if (isSingleDay) {
          const h = d.getHours() % 12 || 12;
          const ampm = d.getHours() < 12 ? 'AM' : 'PM';
          const dateStr = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          return `${dateStr}, ${h} ${ampm}`;
        }
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      });
      const widgets = buildWidgets(curr, prevData, labels, mm1, mm25, mm4, mm10);
      const { pmData, noiseData, tempData, humidityData, co2Data, noxData, vocData } = widgets;

      const st = {
        pm1: statusFor(pmData.pm1.current, THRESHOLDS.pm1, "pm25"), pm25: statusFor(pmData.pm25.current, THRESHOLDS.pm25, "pm25"),
        pm5: statusFor(pmData.pm5.current, THRESHOLDS.pm5, "pm5"), pm10: statusFor(pmData.pm10.current, THRESHOLDS.pm10, "pm10"),
        noise: statusFor(noiseData.current, THRESHOLDS.noise, "noise"), temp: statusFor(tempData.current, THRESHOLDS.temperature),
        humidity: statusFor(humidityData.current, THRESHOLDS.humidity), co2: statusFor(co2Data.current, THRESHOLDS.co2),
        nox: statusFor(noxData.current, THRESHOLDS.nox), voc: statusFor(vocData.current, THRESHOLDS.voc),
      };

      const hasNoise = hourly.some(d => (d.dba || 0) > 0);
      let mod = 0, high = 0, vHigh = 0;
      hourly.forEach(r => {
        const pvals = { pm1: r.pm1p0, pm25: r.pm2p5, pm5: r.pm4p0, pm10: r.pm10p0, noise: r.dba, co2: r.co2 };
        Object.entries(THRESHOLDS).forEach(([k, t]) => {
          if (t == null) return;
          const val = pvals[k] || 0;
          if (val > t * 1.5) vHigh++;
          else if (val > t * 1.2) high++;
          else if (val > t) mod++;
        });
      });

      setDashData({
        pmData, noiseData, tempData, humidityData, co2Data, noxData, vocData, hourlyData: hourly, hasNoise, isSingleDay,
        summary: { moderate: mod, high: high, veryHigh: vHigh },
        table: [
          { parameter: "PM1.0", status: st.pm1, exceedances: hourly.filter(d => (d.pm1p0 || 0) > THRESHOLDS.pm1).length },
          { parameter: "PM2.5", status: st.pm25, exceedances: hourly.filter(d => (d.pm2p5 || 0) > THRESHOLDS.pm25).length },
          { parameter: "PM4.0", status: st.pm5, exceedances: hourly.filter(d => (d.pm4p0 || 0) > THRESHOLDS.pm5).length },
          { parameter: "PM10", status: st.pm10, exceedances: hourly.filter(d => (d.pm10p0 || 0) > THRESHOLDS.pm10).length },
          ...(hasNoise ? [{ parameter: "Noise", status: st.noise, exceedances: hourly.filter(d => (d.dba || 0) > THRESHOLDS.noise).length }] : []),
          { parameter: "Humidity", status: st.humidity, exceedances: hourly.filter(d => (d.humidity || 0) > THRESHOLDS.humidity).length },
          ...(hourly.some(d => (d.co2 || 0) > 0) ? [{ parameter: "CO2", status: st.co2, exceedances: hourly.filter(d => (d.co2 || 0) > THRESHOLDS.co2).length }] : []),
          { parameter: "NOx", status: st.nox, exceedances: THRESHOLDS.nox != null ? hourly.filter(d => (d.nox || 0) > THRESHOLDS.nox).length : "—" },
          { parameter: "VOC", status: st.voc, exceedances: THRESHOLDS.voc != null ? hourly.filter(d => (d.voc || 0) > THRESHOLDS.voc).length : "—" },
        ],
      });
    } catch (err) {
      if (err.response?.status === 404) setError("404: Endpoint not found.");
      else if (err.response?.status === 401) setError("401: Unauthorized.");
      else if (err.code === "ERR_NETWORK") setError("Network error.");
      else setError(`Error: ${err.message}`);
    } finally { setLoading(false); }
  }

  function applyPreset(p) {
    const t = new Date(); let s, e, label;
    switch (p) {
      case "today": s = e = formatDate(t); label = "Today"; break;
      case "week": { const w = new Date(t); w.setDate(w.getDate() - 7); s = formatDate(w); e = formatDate(t); label = "Last 7 Days"; break; }
      case "month": s = formatDate(new Date(t.getFullYear(), t.getMonth(), 1)); e = formatDate(new Date(t.getFullYear(), t.getMonth() + 1, 0)); label = "This Month"; break;
      case "quarter": { const q = Math.floor(t.getMonth() / 3); s = formatDate(new Date(t.getFullYear(), q * 3, 1)); e = formatDate(new Date(t.getFullYear(), q * 3 + 3, 0)); label = "This Quarter"; break; }
      case "year": s = formatDate(new Date(t.getFullYear(), 0, 1)); e = formatDate(new Date(t.getFullYear(), 11, 31)); label = "This Year"; break;
      default: return;
    }
    setStartDate(s); setEndDate(e); setDateLabel(label); setAnchor(null);
  }
  function applyCustomRange() {
    const s = new Date(startDate), e = new Date(endDate), sy = s.getFullYear(), ey = e.getFullYear();
    const sm = s.toLocaleDateString("en-US", { month: "short", day: "numeric" }), em = e.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    setDateLabel(sy === ey ? `${sm} - ${em}, ${sy}` : `${sm}, ${sy} - ${em}, ${ey}`); setAnchor(null);
  }

  const D = dashData;
  const F = forecastData;
  const FC = showForecast && F;

  const pw = (key) => FC ? F.pmData[key] : D?.pmData[key];
  const fw = (key) => FC ? F[key] : D?.[key];

  const dailyExcData = D ? [
    { name: 'PM1.0', key: 'pm1', limit: DAILY_THRESHOLDS.pm1 },
    { name: 'PM2.5', key: 'pm25', limit: DAILY_THRESHOLDS.pm25 },
    { name: 'PM4.0', key: 'pm5', limit: DAILY_THRESHOLDS.pm5 },
    { name: 'PM10', key: 'pm10', limit: DAILY_THRESHOLDS.pm10 },
  ].map(param => {
    const values = pw(param.key)?.values || [];
    const excCount = values.filter(v => v > param.limit).length;
    return { ...param, exceedances: excCount, total: values.length };
  }) : [];

  return (
    <Box onMouseMove={e => { e.currentTarget.style.setProperty('--mouse-x', `${e.clientX}px`); e.currentTarget.style.setProperty('--mouse-y', `${e.clientY}px`); }}
      sx={{ minHeight: "100vh", position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg,#e0f2fe 0%,#dbeafe 50%,#e0e7ff 100%)', p: { xs: 2, md: 3 }, '--mouse-x': '50%', '--mouse-y': '50%' }}>
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: `radial-gradient(280px circle at var(--mouse-x,50%) var(--mouse-y,50%),rgba(59,130,246,0.22),transparent 70%),radial-gradient(500px circle at var(--mouse-x,50%) var(--mouse-y,50%),rgba(99,102,241,0.12),transparent 70%),radial-gradient(800px circle at var(--mouse-x,50%) var(--mouse-y,50%),rgba(14,165,233,0.07),transparent 70%)`, transition: 'background 0.08s ease' }} />
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0, '@keyframes float': { '0%,100%': { transform: 'translate(0,0) scale(1)' }, '33%': { transform: 'translate(30px,-30px) scale(1.1)' }, '66%': { transform: 'translate(-20px,20px) scale(0.9)' } }, '& > div': { position: 'absolute', borderRadius: '50%', filter: 'blur(80px)', opacity: 0.3, animation: 'float 20s ease-in-out infinite' }, '& > div:nth-of-type(1)': { width: '400px', height: '400px', background: 'rgba(59,130,246,0.4)', top: '10%', left: '10%' }, '& > div:nth-of-type(2)': { width: '350px', height: '350px', background: 'rgba(99,102,241,0.3)', top: '60%', right: '10%', animationDelay: '7s' }, '& > div:nth-of-type(3)': { width: '300px', height: '300px', background: 'rgba(14,165,233,0.3)', bottom: '10%', left: '40%', animationDelay: '14s' } }}><div /><div /><div /></Box>
      <Box sx={{ position: 'fixed', inset: 0, backgroundImage: `linear-gradient(rgba(59,130,246,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,0.03) 1px,transparent 1px)`, backgroundSize: '50px 50px', pointerEvents: 'none', zIndex: 0 }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper sx={filterBarSx}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={labelSx}>Sensor</InputLabel>
                <Select value={sensorId} label="Sensor" onChange={e => setSensorId(e.target.value)} sx={selectSx} disabled={loading || stationsLoading}>
                  {stationsLoading ? <MenuItem disabled>Loading sensors...</MenuItem> : sensorOptions.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box onClick={loading ? undefined : e => setAnchor(e.currentTarget)} sx={{ bgcolor: loading ? '#cbd5e1' : '#667eea', color: 'white', borderRadius: 3, px: 2.5, py: 1, border: `2px solid ${loading ? '#cbd5e1' : '#667eea'}`, boxShadow: '0 4px 12px rgba(102,126,234,0.3)', cursor: loading ? 'default' : 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', '&:hover': loading ? {} : { bgcolor: '#5568d3', borderColor: '#5568d3', transform: 'translateY(-2px)' } }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', opacity: 0.8, lineHeight: 1 }}>📅 Click to Select Dates</Typography>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, mt: 0.4, lineHeight: 1.2 }}>{dateLabel}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                <Button variant="outlined" onClick={() => navigate("/hotel-summary")} startIcon={<span>⬅️</span>} sx={{ textTransform: 'none', borderRadius: 3, border: '2px solid #3b82f6', color: '#3b82f6', fontWeight: 600, '&:hover': { bgcolor: 'rgba(59,130,246,0.08)', borderColor: '#2563eb' } }}>
                  Back to Summary
                </Button>
                <Box component="img" src={sacaqmLogo} alt="SACAQM" sx={{ height: 75, objectFit: 'contain', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))' }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button fullWidth onClick={async () => {
                  setDownloadLoading(true);
                  try {
                    const selSensor = sensorOptions.find(o => o.id === sensorId);
                    await generateReport({ dashData: D, sensorId, sensorLabel: selSensor?.label || sensorId, dateLabel, startDate, endDate, thresholds: THRESHOLDS, dailyThresholds: DAILY_THRESHOLDS, dailyExcData, forecastData: F, showForecast, botpressAnalysis: "" });
                  } catch(e) {
                    console.error('Report generation failed:', e);
                  } finally {
                    setDownloadLoading(false);
                  }
                }} disabled={downloadLoading || !D} startIcon={downloadLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <span>⬇️</span>} sx={{ bgcolor: '#0ea5e9', color: 'white', borderRadius: 3, py: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#0284c7', transform: 'translateY(-2px)' }, '&:disabled': { bgcolor: '#cbd5e1', color: 'white' } }}>
                  {downloadLoading ? 'Generating...' : 'Download Report'}
                </Button>
                <Button fullWidth onClick={() => setShowForecast(p => !p)} disabled={!D || forecastLoading || !!forecastError} startIcon={forecastLoading ? <CircularProgress size={14} sx={{ color: '#8b5cf6' }} /> : <span>🔮</span>} sx={{ bgcolor: showForecast ? '#8b5cf6' : 'white', color: showForecast ? 'white' : '#8b5cf6', borderRadius: 3, py: 1, fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', border: '2px solid #8b5cf6', '&:hover': { bgcolor: showForecast ? '#7c3aed' : 'rgba(139,92,246,0.08)', transform: 'translateY(-2px)' }, '&:disabled': { bgcolor: '#f1f5f9', borderColor: '#e2e8f0', color: '#94a3b8' } }}>
                  {forecastLoading ? 'AI Thinking...' : showForecast ? 'Hide Forecast' : 'Show Forecast'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }} sx={{ "& .MuiPaper-root": { borderRadius: 3, boxShadow: "0 12px 40px rgba(0,0,0,0.15)", p: 3, mt: 1 } }}>
          <Box sx={{ minWidth: 450 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#1e293b", fontSize: '1.1rem' }}>📅 Select Date Range</Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>Quick Selection</Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
              {[["today", "Today"], ["week", "Last 7 Days"], ["month", "This Month"], ["quarter", "This Quarter"], ["year", "This Year"]].map(([p, l]) => (
                <Button key={p} size="small" onClick={() => applyPreset(p)} sx={{ ...presetSx, minWidth: 80 }}>{l}</Button>
              ))}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>Custom Range</Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField label="Start Date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' } } }} />
              <TextField label="End Date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} inputProps={{ min: startDate }} fullWidth size="small" InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' } } }} />
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContext: "flex-end" }}>
              <Button onClick={() => setAnchor(null)} sx={{ textTransform: "none", color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>Cancel</Button>
              <Button variant="contained" onClick={applyCustomRange} sx={{ bgcolor: "#667eea", textTransform: "none", px: 4, fontWeight: 600, "&:hover": { bgcolor: "#5568d3" } }}>Apply</Button>
            </Box>
          </Box>
        </Popover>

        {loading && <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", my: 8 }}><CircularProgress size={60} sx={{ color: "#3b82f6" }} /><Typography sx={{ mt: 2, color: "#3b82f6", fontWeight: 600 }}>Loading dashboard data...</Typography></Box>}
        {error && !loading && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {forecastError && !loading && <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, border: '1px solid #fcd34d' }}><strong>Forecast Disabled:</strong> {forecastError}</Alert>}

        {!loading && D && (
          <>
            {showForecast && (
              <Box sx={{ mb: 3, p: 2.5, borderRadius: 3, background: 'linear-gradient(135deg,rgba(139,92,246,0.14),rgba(99,102,241,0.1))', border: '1.5px solid rgba(139,92,246,0.35)', display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 4px 20px rgba(139,92,246,0.12)' }}>
                <span style={{ fontSize: '1.8rem' }}>🤖</span>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#5b21b6' }}>AI Forecast Mode Active</Typography>
                    <Box sx={{ px: 1.5, py: 0.25, borderRadius: 10, background: 'linear-gradient(90deg,#7c3aed,#6366f1)', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <span style={{ fontSize: '0.7rem' }}>✦</span>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'white', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{F?.modelName || 'AI-Powered'}</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: '0.95rem', color: '#6d28d9', mt: 0.4 }}>
                    Showing AI-generated hourly forecasts for next 24 hours&nbsp;
                    <Box component="span" sx={{ fontWeight: 700, color: '#5b21b6' }}>({FORECAST_DAY_LABEL})</Box>
                  </Typography>
                </Box>
              </Box>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}><Grid item xs={12}><Box sx={fadeIn(0)}>
              <ExceedancesTable hourlyData={FC ? F.hourlyData : D.hourlyData} thresholds={THRESHOLDS} isForecast={showForecast} forecastHourLabels={FORECAST_HOUR_LABELS} hasNoise={FC ? F.hasNoise : D.hasNoise} />
            </Box></Grid></Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}><Grid item xs={12}><Box sx={fadeIn(0.1)}>
              <ExceedancesOverTimeChart hourlyData={FC ? F.hourlyData : D.hourlyData} thresholds={THRESHOLDS} isForecast={showForecast} forecastHourLabels={FORECAST_HOUR_LABELS} forecastDayLabel={showForecast ? FORECAST_DAY_LABEL : null} hasNoise={FC ? F.hasNoise : D.hasNoise} isSingleDay={D.isSingleDay} />
            </Box></Grid></Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {[{ severity: "moderate", title: "Moderate Exceedances", color: "#fbbf24", delay: 0.15 }, { severity: "high", title: "High Exceedances", color: "#fb923c", delay: 0.16 }, { severity: "veryHigh", title: "Very High Exceedances", color: "#ef4444", delay: 0.17 }].map(({ severity, title, color, delay }) => (
                <Grid item xs={12} md={4} key={severity}><Box sx={fadeIn(delay)}>
                  <ExceedancesSeverityChart hourlyData={FC ? F.hourlyData : D.hourlyData} thresholds={THRESHOLDS} severity={severity} title={showForecast ? `${title} (Forecast)` : title} color={color} isForecast={showForecast} forecastHourLabels={FORECAST_HOUR_LABELS} hasNoise={FC ? F.hasNoise : D.hasNoise} />
                </Box></Grid>
              ))}
            </Grid>

            {!showForecast && (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Box sx={fadeIn(0.18)}>
                    <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', bgcolor: 'white' }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#1e293b' }}>
                        Daily PM Exceedances
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                              <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Parameter</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Daily Limit</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Exceedance Days</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dailyExcData.map(row => (
                              <TableRow key={row.key}>
                                <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                                <TableCell>{row.limit} µg/m³</TableCell>
                                <TableCell>
                                  <Chip
                                    label={`${row.exceedances} / ${row.total} Days`}
                                    size="small"
                                    sx={{
                                      bgcolor: row.exceedances > 0 ? '#fee2e2' : '#dcfce7',
                                      color: row.exceedances > 0 ? '#dc2626' : '#16a34a',
                                      fontWeight: 700
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Box>
                </Grid>
              </Grid>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {Object.entries(D.pmData).map(([key, widget], idx) => {
                const d = pw(key) || widget;
                return (<Grid item xs={12} sm={6} lg={3} key={key}><Box sx={fadeIn(0.2 + idx * 0.1)}>
                  <PMWidget
                    title={showForecast ? `${widget.title} (Forecast)` : widget.title}
                    labels={d.labels}
                    dataPoints={d.values}
                    threshold={DAILY_THRESHOLDS[key]}
                    trend={widget.trend}
                    unit="µg/m³"
                  />
                </Box></Grid>);
              })}
            </Grid>

            {(FC ? F.hasNoise : D.hasNoise) && (
              <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
                <Grid item xs={12} md={4} sx={{ display: 'flex' }}><Box sx={{ ...fadeIn(0.6), display: 'flex', flex: 1, width: '100%' }}>
                  <NoiseGauge value={FC ? F.noiseData.current : (realtimeNoise ?? D.noiseData.current)} subLabel={showForecast ? "Forecast Period Average" : "Daily Average"} />
                </Box></Grid>
                <Grid item xs={12} md={8} sx={{ display: 'flex' }}><Box sx={{ ...fadeIn(0.65), display: 'flex', flex: 1, width: '100%' }}>
                  {(() => { const d = fw("noiseData"); return <NoiseWidget title={showForecast ? "Noise Levels (Forecast)" : "Noise Levels Over Time"} labels={d.labels} data={d.values} threshold={THRESHOLDS.noise} />; })()}
                </Box></Grid>
              </Grid>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={D.co2Data.values.some(v => v > 0) ? 4 : 6}><Box sx={fadeIn(0.7)}>
                {(() => { const d = fw("tempData"); return <TempWidget title={showForecast ? "Temperature (Forecast)" : "Temperature"} labels={d.labels} data={d.values} threshold={THRESHOLDS.temperature} />; })()}
              </Box></Grid>
              <Grid item xs={12} md={D.co2Data.values.some(v => v > 0) ? 4 : 6}><Box sx={fadeIn(0.8)}>
                {(() => { const d = fw("humidityData"); return <ParameterWidget title={showForecast ? "Humidity (Forecast)" : "Humidity"} labels={d.labels} data={d.values} threshold={THRESHOLDS.humidity} unit="%" />; })()}
              </Box></Grid>
              {D.co2Data.values.some(v => v > 0) && (<Grid item xs={12} md={4}><Box sx={fadeIn(0.9)}>
                {(() => { const d = fw("co2Data"); return <ParameterWidget title={showForecast ? "CO2 (Forecast)" : "CO2"} labels={d.labels} data={d.values} threshold={THRESHOLDS.co2} unit=" ppm" />; })()}
              </Box></Grid>)}
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}><Box sx={fadeIn(1.0)}>
                {(() => { const d = fw("noxData"); return <ParameterWidget title={showForecast ? "NOx (Forecast)" : "NOx"} labels={d.labels} data={d.values} threshold={THRESHOLDS.nox} unit="" />; })()}
              </Box></Grid>
              <Grid item xs={12} md={6}><Box sx={fadeIn(1.1)}>
                {(() => { const d = fw("vocData"); return <ParameterWidget title={showForecast ? "VOC (Forecast)" : "VOC"} labels={d.labels} data={d.values} threshold={THRESHOLDS.voc} unit="" />; })()}
              </Box></Grid>
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
}